import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { useSeedDemo } from "@/hooks/useDemo";
import type { DemoRow, DemoSeedResult } from "@/interfaces/demo";

interface DemoSeedDialogProps {
  /** The row to seed, or null when closed. */
  row: DemoRow | null;
  onClose: () => void;
}

/**
 * Confirm-then-run for Seed / Reseed. A reseed always drops and recreates the organization — a
 * demo org has no state worth preserving — and this dialog is where the super admin is told so
 * before anything happens. Stays open on success to show what the seed had to adapt (collection
 * and variant remaps, an adopted login row), then closes on acknowledgement.
 */
export default function DemoSeedDialog({ row, onClose }: DemoSeedDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DemoSeedResult | null>(null);
  const { mutate: seed, isPending } = useSeedDemo();

  const reseed = row?.seeded !== null && row?.seeded !== undefined;

  const handleClose = () => {
    if (isPending) return;
    setError(null);
    setResult(null);
    onClose();
  };

  const handleSeed = () => {
    if (!row) return;
    setError(null);
    seed(row.slug, {
      onSuccess: (res) => setResult(res),
      onError: (err) => setError(err instanceof Error ? err.message : "Seeding failed."),
    });
  };

  return (
    <Dialog
      open={!!row}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            p: "32px",
            width: 520,
            maxWidth: "calc(100vw - 40px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            position: "relative",
          },
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        disabled={isPending}
        aria-label="Close"
        sx={{
          position: "absolute",
          top: 20,
          right: 20,
          border: "1px solid var(--color-smoke)",
          borderRadius: "var(--radius-btn)",
          color: "var(--color-graphite)",
          width: 36,
          height: 36,
          "&:hover": { bgcolor: "var(--color-wax)", color: "var(--color-rodeo-black)" },
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Typography
        sx={{
          fontFamily: "var(--font-crimson)",
          fontSize: "24px",
          fontWeight: 400,
          color: "var(--color-rodeo-black)",
          mb: "18px",
        }}
      >
        {result ? "Seed complete" : reseed ? `Reseed ${row?.orgName}` : `Seed ${row?.orgName}`}
      </Typography>

      {result ? (
        <Box sx={{ fontSize: "13.5px", color: "var(--color-rodeo-black)", lineHeight: 1.6 }}>
          <Typography sx={{ fontSize: "inherit", mb: "10px" }}>
            <strong>{row?.orgName}</strong> is organization #{result.organizationId}: {result.users}{" "}
            user{result.users === 1 ? "" : "s"}, {result.campaigns} campaign
            {result.campaigns === 1 ? "" : "s"}, {result.contacts.toLocaleString()} contacts. Login{" "}
            <code>{result.loginEmail}</code>
            {result.clerkUserId ? "" : " (no Clerk account — CLERK_SECRET_KEY unset?)"}.
          </Typography>
          {result.notes.length > 0 && (
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: "18px",
                fontSize: "12.5px",
                color: "var(--color-graphite)",
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {result.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            bgcolor: "var(--color-wax)",
            borderRadius: "10px",
            p: "12px 14px",
            mb: "8px",
          }}
        >
          <WarningAmberIcon
            sx={{ fontSize: "20px", color: "var(--color-dark-dust)", flexShrink: 0 }}
          />
          <Typography
            sx={{ fontSize: "13.5px", color: "var(--color-rodeo-black)", lineHeight: 1.5 }}
          >
            {reseed ? (
              <>
                This <strong>drops organization #{row?.seeded?.organizationId}</strong> — its users,
                campaigns, stats and contacts — and rebuilds <strong>{row?.orgName}</strong> from
                the committed bundle (extracted{" "}
                {row ? new Date(row.extractedAt).toLocaleDateString() : ""}). Anyone signed in as
                its demo login is signed out of the old organization. This runs against{" "}
                <strong>this environment's</strong> database.
              </>
            ) : (
              <>
                Creates <strong>{row?.orgName}</strong> as a demo organization in{" "}
                <strong>this environment</strong> from the committed bundle: {row?.users} user
                {row?.users === 1 ? "" : "s"}, {row?.campaigns} campaigns,{" "}
                {row?.contacts.toLocaleString()} contacts, and the login{" "}
                <code>{row?.loginEmail}</code> on this environment's Clerk instance.
              </>
            )}
          </Typography>
        </Box>
      )}

      {error && (
        <Typography
          sx={{
            mt: "8px",
            fontSize: "13px",
            color: "var(--color-good-wine)",
            bgcolor: "var(--color-good-wine-bg)",
            border: "1px solid var(--color-good-wine-4)",
            borderRadius: "8px",
            p: "10px 12px",
          }}
        >
          {error}
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", mt: "22px" }}>
        {result ? (
          <Button onClick={handleClose} disableRipple disableElevation sx={primaryButtonSx}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isPending} disableRipple sx={secondaryButtonSx}>
              Cancel
            </Button>
            <Button
              onClick={handleSeed}
              disabled={isPending}
              disableRipple
              disableElevation
              sx={reseed ? destructiveButtonSx : primaryButtonSx}
            >
              {isPending ? "Seeding…" : reseed ? "Drop and reseed" : "Seed"}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
}

const buttonBaseSx = {
  fontFamily: "var(--font-lato)",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  borderRadius: "6px",
  px: "20px",
  py: "10px",
  "&.Mui-disabled": { opacity: 0.45 },
} as const;

const secondaryButtonSx = {
  ...buttonBaseSx,
  color: "var(--color-rodeo-black)",
  bgcolor: "var(--color-white)",
  border: "1px solid var(--color-smoke)",
  "&:hover": { borderColor: "var(--color-dark-dust)", bgcolor: "var(--color-white)" },
} as const;

const primaryButtonSx = {
  ...buttonBaseSx,
  bgcolor: "var(--color-rodeo-black)",
  color: "var(--color-white)",
  "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
  "&.Mui-disabled": { opacity: 0.45, color: "var(--color-white)" },
} as const;

const destructiveButtonSx = {
  ...buttonBaseSx,
  bgcolor: "var(--color-good-wine)",
  color: "var(--color-white)",
  "&:hover": { bgcolor: "var(--color-good-wine-2)" },
  "&.Mui-disabled": { opacity: 0.45, color: "var(--color-white)" },
} as const;
